import * as React from "react";
import { ClientResources, CaseRunClient, CalculateInfoModel, CaseRunTaskModel, SentryCaseRunDetailReadModel, CaseRunDetailReadModel, BrutusCaseRunDetailReadModel, BrutusGender, ContributorReadModel, BrutusTestType, LabReportsTextsType } from "@app/api2/api";
import { Grid, GridColumn, GridRow, Table, TableBody, TableCell, TableRow } from "@/components/bp/bp-ui";
import { ParametersView } from "../ParametersView";
import { HandlerRegistry } from "../RunTypes/HandlerRegistry";
import { InfoBox } from "@common/InfoBox";
import { elapsedMinToString, elapsedSecToString, getInactiveFilter, getRunStatusAsString, insertComponentsIntoText, toLocalTime } from "@common/Utils";
import { BPBoxWithHeader } from "@common/BPBoxWithHeader";
import { ReactNode } from "react";
import { CaseRunNotes } from "../CaseRunNotes";
import { BPDataTable3 } from "../../Common/DataTable/BPDataTable3";
import { BPColumn } from "@common/BPReactTable";
import { buildUrl, AppRoutes } from "../../AppRoutes";
import { CalculationDetailsModal } from "./CalculationDetailsModal";
import { AicCalculations } from "./AicCalculations";
import { PrintContext } from "../Print/PrintContext";
import { NoteTemplates } from "../NoteTemplates";
import { PedigreeEditor } from "../RunTypes/Brutus/PedigreeEditor";
import { TranslationContext } from "../../Contexts/TranslationContext";
import { Route, Routes as RouterRoutes } from "react-router";
import { RouteContent } from "@common/RouteContent";
import { Link } from "react-router-dom";

export interface ICaseRunDetailsPaneProps {
    model: CaseRunDetailReadModel;
    caseUrl: string | undefined;
    populationUrl: string | undefined;
    referenceUrls?: { [key: string]: string; } | undefined;
    evidenceUrls?: { [key: string]: string; } | undefined;
}

export function CaseRunDetailsPane(props: ICaseRunDetailsPaneProps) {
    const isAic = props.model.runType == 'maxlr_aic';
    const notes = props.model.description;
    const isDeconv = props.model.runType == 'deconv_main';
    const isBrutus = props.model.runType == 'brutus';
    const brutusModel = props.model as BrutusCaseRunDetailReadModel;
    const calcIndependently = props.model.parameters.find(x => x.parameter == "calcAicIndependently")?.value == "1"

    const isBrutusFreeform = isBrutus && brutusModel.testType == BrutusTestType.Freeform;
    // const isUhr = isBrutus && brutusModel.testType == BrutusTestType.UHR;
    // const isKinship = isBrutus && brutusModel.testType == BrutusTestType.Kinship;


    const hasReportTemplates = ['maxlr', 'maxlr_aic', 'brutus'].indexOf(props.model.runType) > -1

    const caseRunClient = new CaseRunClient();
    const printOptions = React.useContext(PrintContext)
    const forPrint = printOptions != null;


    return forPrint ? (<>
        {
            printOptions.caseRunDetails &&
            <table className="two-col-layout">
                <tbody>
                    <tr>
                        <td>
                            <CaseRunGeneralInfoBox {...props} />
                            <CaseRunStatusBox {...props} />
                            <CaseRunContributorsAndEvidence {...props} />
                        </td>
                        <td>
                            <ParametersView
                                forPrint={forPrint}
                                model={props.model}
                                parameters={props.model.parameters} parameterRenderer={props.model ? HandlerRegistry.getRunTypeHandler(props.model.runType) : null} />

                        </td>
                    </tr>
                </tbody>
            </table>
        }
        {
            printOptions.caseRunNotes &&
            <CaseRunNotes forPrint={forPrint} caseId={props.model.efmCaseId} caseRunId={props.model.caseRunId} labId={props.model.labId} notes={notes} />
        }
        {printOptions.aicRuns && (isAic || isDeconv) &&
            <BPBoxWithHeader forPrint={forPrint} header={window.translation.caseRunView.aicRuns}>
                {AicCalculations({ separateHpHd: calcIndependently, isDeconv: isDeconv, labId: props.model.labId, caseRunId: props.model.caseRunId, caseId: props.model.efmCaseId, forPrint: forPrint })}
            </BPBoxWithHeader>
        }
    </>) : (<TranslationContext.Consumer>{t =>
        <Grid>
            <GridRow>
                <GridColumn width={5}>
                    <Grid>
                        <GridRow>
                            <GridColumn>
                                <CaseRunGeneralInfoBox {...props} />
                            </GridColumn>
                        </GridRow>
                        <GridRow>
                            <GridColumn>
                                <CaseRunStatusBox {...props} />
                            </GridColumn>
                        </GridRow>
                    </Grid>
                </GridColumn>
                <GridColumn width={5}>
                    <CaseRunContributorsAndEvidence {...props} />
                </GridColumn>
                <GridColumn width={6} clearing='both'>
                    <ParametersView
                        forPrint={forPrint}
                        model={props.model}
                        parameters={props.model.parameters} parameterRenderer={props.model ? HandlerRegistry.getRunTypeHandler(props.model.runType) : null} />
                </GridColumn>
            </GridRow>
            <GridRow>
                <GridColumn width={16}>
                    <CaseRunNotes forPrint={forPrint} caseId={props.model.efmCaseId} caseRunId={props.model.caseRunId} labId={props.model.labId} notes={notes} />
                </GridColumn>
            </GridRow>
            {hasReportTemplates &&
                <GridRow>
                    <GridColumn width={16}>
                        <NoteTemplates reportType={isBrutus ? LabReportsTextsType.Brutus : LabReportsTextsType.EFM} caseId={props.model.efmCaseId} caseRunId={props.model.caseRunId} labId={props.model.labId} />
                    </GridColumn>
                </GridRow>
            }
            {isBrutus &&
                <GridRow>
                    <GridColumn width={16}>
                        <BPBoxWithHeader forPrint={forPrint} header={t.brutus.pedigree}>
                            {isBrutusFreeform &&
                                <Grid>
                                    <GridRow columns={2}>
                                        <GridColumn>
                                            <PedigreeEditor
                                                testType={brutusModel.testType}
                                                readonly
                                                references={brutusModel.h1.references.map(x => { return { id: x.sampleId, isMale: x.gender == BrutusGender.Male, isMissingPerson: x.isMissing, isUntested: x.isUntested, selected: true, name: x.name } })}
                                                relations={brutusModel.h1.relations.map(x => { return { ancestorId: x.ancestorId, descendantId: x.descendantId, questioned: x.isQuestioned } })} />
                                        </GridColumn>
                                        <GridColumn>
                                            <PedigreeEditor
                                                testType={brutusModel.testType}
                                                readonly
                                                references={brutusModel.h2.references.map(x => { return { id: x.sampleId, isMale: x.gender == BrutusGender.Male, isMissingPerson: x.isMissing, isUntested: x.isUntested, selected: true, name: x.name } })}
                                                relations={brutusModel.h2.relations.map(x => { return { ancestorId: x.ancestorId, descendantId: x.descendantId, questioned: x.isQuestioned } })} />
                                        </GridColumn>
                                    </GridRow>
                                </Grid>
                            }
                            {isBrutus && !isBrutusFreeform &&
                                <Grid>
                                    <GridRow columns={1}>
                                        <GridColumn>
                                            <PedigreeEditor
                                                testType={brutusModel.testType}
                                                readonly
                                                references={brutusModel.references.map(x => { return { id: x.sampleId, isMale: x.gender == BrutusGender.Male, isMissingPerson: x.isMissing, isUntested: x.isUntested, selected: true, name: x.name } })}
                                                relations={brutusModel.relations.map(x => { return { ancestorId: x.ancestorId, descendantId: x.descendantId, questioned: x.isQuestioned } })} />
                                        </GridColumn>
                                    </GridRow>
                                </Grid>
                            }
                        </BPBoxWithHeader>
                    </GridColumn>
                </GridRow>
            }
            {/* {props.model.aic &&
                <GridRow>
                    <GridColumn width={16}>
                        <BPBoxWithHeader header={t.caseRunView.aicRuns}>
                            <BPDataTable3
                                forPrint={forPrint}
                                defaultSortAsc={false}
                                defaultSortField="adjLogLik"
                                dataSource={(params) => {
                                    return caseRunClient.getOldAicCalculations(props.model.labId, props.model.caseId, props.model.caseRunId, params.perPage, params.page, params.sortBy, params.sortDirection);
                                }}
                                columns={[
                                    { Header: 'id', accessor: 'id' },
                                    { Header: t.common.status, accessor: 'state', Cell: cell => cell.value },
                                    { Header: t.caseRunView.hpUnknowns, accessor: 'hpUnknowns', Cell: cell => cell.value },
                                    { Header: t.caseRunView.hdUnknowns, accessor: 'hdUnknowns', Cell: cell => cell.value },
                                    { Header: t.caseRunView.useDegradation, accessor: 'useDegradation' },
                                    { Header: t.caseRunView.useStutter, accessor: 'useStutter' },
                                    { Header: t.caseRunView.phVariability, accessor: 'phVariability' },
                                    { Header: t.caseRunView.likelihoodRatioLog10Short, accessor: 'likelihoodRatioLog10' },
                                    { Header: t.caseRunView.aicScore, accessor: 'aicD' },
                                ] as Array<BPColumn<AicCaseRunModel>>} />
                        </BPBoxWithHeader>
                    </GridColumn>
                </GridRow>} */}
            {(isAic || isDeconv) &&
                <GridRow>
                    <GridColumn width={16}>
                        <BPBoxWithHeader forPrint={forPrint} header={t.caseRunView.aicRuns}>
                            {AicCalculations({ separateHpHd: calcIndependently, isDeconv: isDeconv, labId: props.model.labId, caseRunId: props.model.caseRunId, caseId: props.model.efmCaseId, forPrint: forPrint })}
                        </BPBoxWithHeader>
                    </GridColumn>
                </GridRow>}
            {!forPrint && <>
                <GridRow>
                    <GridColumn width={16}>
                        {props.model.canCreateRunTypes.length > 0 &&
                            <BPBoxWithHeader forPrint={forPrint} header={t.caseRunView.additionalCalculations}>
                                <BPDataTable3
                                    forPrint={forPrint}
                                    defaultSortField="created"
                                    defaultSortAsc={false}
                                    dataSource={async (params) => {
                                        let showInactive = params.filters.find(x => x.id == 'showInactive' && x.value)?.value ?? false;
                                        return await caseRunClient.getCalculations(props.model.labId, props.model.efmCaseId, props.model.caseRunId, params.perPage, params.page, params.sortBy, params.sortDirection, showInactive)
                                    }}
                                    columns={[
                                        getInactiveFilter('showInactive', window.translation.caseRun.showInactiveCalculations),
                                        {
                                            Header: t.caseRunView.calculationType, accessor: 'runTypeFriendly', Cell: cell => <Link to={buildUrl(AppRoutes.CaseRunCalculationDetails, {
                                                labId: props.model.labId.toString(),
                                                caseId: props.model.efmCaseId.toString(),
                                                caseRunId: props.model.caseRunId.toString(),
                                                calculationId: cell.row.original.id.toString(),
                                                // urlb64: btoa(trimStart(cell.row.original.viewUrl, '/') || '')
                                            })}>{cell.value}</Link>
                                        },
                                        { Header: t.common.status, accessor: 'status', Cell: cell => getRunStatusAsString(cell.value) },
                                        { Header: t.caseRunView.timeUsed, accessor: 'timeTakenMinutes', Cell: cell => elapsedMinToString(cell.value) },
                                        { Header: t.common.created, accessor: 'created', Cell: cell => toLocalTime(cell.value) }
                                    ] as Array<BPColumn<CalculateInfoModel>>} />
                                <RouterRoutes>
                                    <Route path={':calculationId/detail'} element={<RouteContent render={routeProps => <CalculationDetailsModal header={t.caseRunView.calculationDetails} closeButtonLabel={t.common.close} goBack={() => routeProps.navigate(buildUrl(AppRoutes.CaseRunDetails, {
                                        caseId: props.model.efmCaseId.toString(),
                                        caseRunId: props.model.caseRunId.toString(),
                                        labId: props.model.labId.toString(),
                                    }))} calculationId={parseInt(routeProps.params.calculationId)} caseId={props.model.efmCaseId} labId={props.model.labId} caseRunId={props.model.caseRunId} />} />} />
                                </RouterRoutes>
                            </BPBoxWithHeader>}
                    </GridColumn>
                </GridRow>
                <GridRow>
                    <GridColumn width={16}>
                        <BPBoxWithHeader forPrint={forPrint} header={t.caseRunView.calculationTasks}>
                            <BPDataTable3
                                simple
                                dataSource={(params) => {
                                    return Promise.resolve({ items: props.model.tasks, totalRows: 0 });
                                }}
                                columns={[
                                    { Header: t.caseRunView.step, accessor: 'phase', Cell: cell => t.jobPhase[cell.value] },
                                    { Header: t.common.created, accessor: 'started', Cell: cell => toLocalTime(cell.value) },
                                    { Header: t.common.completed, accessor: 'completed', Cell: cell => toLocalTime(cell.value) },
                                    { Header: t.caseRunView.timeUsed, accessor: 'timeTakenSeconds', Cell: cell => elapsedSecToString(cell.value) },
                                ] as Array<BPColumn<CaseRunTaskModel>>} />
                        </BPBoxWithHeader>
                    </GridColumn>
                </GridRow>
            </>}
        </Grid>
    }</TranslationContext.Consumer>)
}

function CaseRunStatusBox(props: ICaseRunDetailsPaneProps) {
    const printOptions = React.useContext(PrintContext)
    const t = window.translation;

    return <InfoBox
        forPrint={printOptions != null}
        header={t.common.status}
        items={[
            { label: t.common.created, description: toLocalTime(props.model.created), width: 6 },
            { label: t.common.completed, description: toLocalTime(props.model.completed) },
            ...props.model.failed ? [{ label: "Failed", description: toLocalTime(props.model.failed) }] : [],
            { label: t.common.owner, description: props.model.createdBy, title: props.model.createdBy },
            ...props.model.computeTimeUsed ? [{ label: t.caseRunView.timeUsed, description: elapsedMinToString(props.model.computeTimeUsed) }] : [],
        ]} />;
}

function CaseRunGeneralInfoBox(props: ICaseRunDetailsPaneProps) {
    const t = window.translation;
    const printOptions = React.useContext(PrintContext)
    const isDeconv = props.model.runType == 'deconv_main';
    const isSentry = props.model.runType == "dnamatch";
    const isBrutus = props.model.runType == "brutus";
    const isFamilialSearch = props.model.runType == "familial_search";
    const brutusModel = props.model as BrutusCaseRunDetailReadModel;

    var showHdUnknowns = !isDeconv && !isSentry && !isBrutus && !isFamilialSearch;
    var showHpUnknowns = !isSentry && !isBrutus && !isFamilialSearch;


    return <InfoBox
        forPrint={printOptions != null}
        header={t.common.information}
        items={[
            { label: t.common.runType, description: t.runType[props.model.runType], width: 6 },
            ...isBrutus ? [{ label: t.brutus.testType, description: BrutusTestType[brutusModel.testType] }] : [],
            { label: t.common.case, description: <a href={props.caseUrl}>{props.model.caseName}</a> },
            ...showHpUnknowns ? [{ label: !isDeconv ? t.caseRun.hpUnknowns : t.common.unknowns, description: props.model['hpUnknowns'] }] : [],
            ...showHdUnknowns ? [{ label: t.caseRun.hdUnknowns, description: props.model['hdUnknowns'] }] : [],
            { label: t.common.population, description: <a href={props.populationUrl}>{props.model.populationName}</a> },
            ...props.model.parentRunId && !(printOptions != null) ? [{ label: t.caseRunView.copiedFrom, description: <Link to={buildUrl(AppRoutes.CaseRunDetails, { caseId: props.model.efmCaseId, caseRunId: props.model.parentRunId, labId: props.model.labId })}>{props.model.parentRunId}</Link> }] : [],
        ]} />;
}

function CaseRunContributorsAndEvidence(props: ICaseRunDetailsPaneProps) {
    const t = window.translation;
    const printOptions = React.useContext(PrintContext)
    const isDeconv = props.model.runType == 'deconv_main';
    const isSentry = props.model.runType == "dnamatch";
    const isBrutus = props.model.runType == "brutus";
    const isFamilialSearch = props.model.runType == "familial_search";
    const isGenSample = props.model.runType == "gen_sample";
    let qualityCases = props.model.runType == "dnamatch" ? ((props.model as SentryCaseRunDetailReadModel)?.qualityCheckCases ?? []/*?.filter(x => x.selected)*/) : [];
    const hasQuality = qualityCases.length > 0;
    const hasEvidence = !isBrutus && !isFamilialSearch && !isGenSample;
    const hasHd = !isDeconv && !isSentry && !isBrutus && !isFamilialSearch;
    const hasHp = !isSentry && !isBrutus && !isFamilialSearch && !isGenSample;

    const brutusModel = props.model as BrutusCaseRunDetailReadModel
    return <>
        {isBrutus &&
            <>
                <ContrInfoBox forPrint={printOptions != null} {...{
                    t: t,
                    items: (brutusModel.h1?.references ?? brutusModel.references).map(r => { return { id: r.sampleId, name: r.name, role: "p" } }),
                    header: t.common.contributors,
                    referenceUrls: props.referenceUrls,
                    evidenceUrls: null,
                    unknowns: brutusModel.h1?.references.filter(r => r.isUntested).length
                }} />
                {brutusModel.h2 != null &&
                    <ContrInfoBox forPrint={printOptions != null} {...{
                        t: t,
                        items: (brutusModel.h2?.references ?? brutusModel.references).map(r => { return { id: r.sampleId, name: r.name, role: "p" } }),
                        header: t.common.contributors,
                        referenceUrls: props.referenceUrls,
                        evidenceUrls: null,
                        unknowns: 0//brutusModel.h2?.references.filter(r => r.isUntested).length
                    }} />
                }
            </>
        }
        {hasHp &&
            <ContrInfoBox forPrint={printOptions != null} {...{
                t: t,
                items: props.model.contributors.filter(x => x.role == 'p'),
                header: <span dangerouslySetInnerHTML={{ __html: isDeconv ? t.common.contributors : t.common.hpContributors }}></span>, referenceUrls: props.referenceUrls,
                evidenceUrls: null,
                unknowns: props.model['hpUnknowns']
            }} />
        }

        {(isSentry || isFamilialSearch) &&
            <ContrInfoBox forPrint={printOptions != null} {...{
                t: t,
                items: props.model.contributors,
                header: t.common.referencePlural,
                referenceUrls: props.referenceUrls,
                evidenceUrls: null,
                unknowns: 0
            }} />
        }
        {hasHd &&
            <ContrInfoBox forPrint={printOptions != null} {...{
                t: t,
                items: props.model.contributors.filter(x => x.role == 'd'),
                header: <span dangerouslySetInnerHTML={{ __html: t.common.hdContributors }}></span>, referenceUrls: props.referenceUrls,
                evidenceUrls: null,
                unknowns: props.model['hdUnknowns'],
                hdRelation: props.model['hdKnownReferenceId'] ? {
                    relName: props.model['hdKnownReferenceRelation'],
                    relSampleId: props.model['hdKnownReferenceId'],
                    relSampleName: props.model.contributors.filter(x => x.id == props.model['hdKnownReferenceId']).map(x => x.name).find(x => true)
                } : null
            }} />
        }
        {hasEvidence &&
            <ContrInfoBox forPrint={printOptions != null} {...{
                t: t,
                items: props.model.evidence,
                header: t.common.evidence,
                referenceUrls: null,
                evidenceUrls: props.evidenceUrls,
                unknowns: 0
            }} />
        }


        {hasQuality &&
            <ContrInfoBox forPrint={printOptions != null} {...{
                t: t,
                items: qualityCases.map(x => { return { ...x, id: x.caseId }; }),
                header: t.common.qualityControlCases,
                referenceUrls: null,
                evidenceUrls: null,
                unknowns: 0
            }} />
        }
    </>;
}

interface IContrInfoBoxProps {
    t: ClientResources;
    items: ContributorReadModel[];
    header: string | ReactNode;
    referenceUrls: {};
    evidenceUrls: {};
    unknowns: number;
    hdRelation?: { relSampleId?: number; relName?: string; relSampleName?: string; };
    forPrint?: boolean;
}

const ContrInfoBox: React.FunctionComponent<IContrInfoBoxProps> = ({ t, items, header, referenceUrls, evidenceUrls, unknowns, hdRelation, forPrint }) => {
    const urls = referenceUrls || evidenceUrls;
    const relName = hdRelation != null ? t.relations[hdRelation.relName] : "";
    const style = !forPrint ? { 'maxHeight': '20vh', 'overflowY': 'auto' } : {};

    return <>
        <>
            <BPBoxWithHeader header={header} forPrint={forPrint}>
                <div style={style as any}>
                    <Table compact definition>
                        <TableBody>
                            {forPrint ? <TableRow><TableCell>{items.map(x => x.name).join(', ')}</TableCell></TableRow> : items.map(m => {
                                return (
                                    <TableRow key={`row-${m.id}`}>
                                        <TableCell>
                                            {urls &&
                                                <a href={urls[m.id]}>{m.name}</a>}
                                            {/* <Link to={urls[m.id]}>{m.name}</Link>} */}
                                            {!urls && m.name}
                                        </TableCell></TableRow>
                                );
                            })}

                            {unknowns > 0 &&
                                <TableRow key={`row-unknowns`}>
                                    <TableCell>
                                        <div>
                                            {items.length ? "+" : ""} {`${unknowns} ${t.common.unknowns}`}
                                        </div>
                                        {hdRelation &&
                                            <div>
                                                {insertComponentsIntoText(t.caseRunView.firstUnknownRelation, {
                                                    '{relationName}': relName
                                                })}&nbsp;
                                                <a href={urls[hdRelation.relSampleId]}>{hdRelation.relSampleName}</a>
                                            </div>}
                                    </TableCell>
                                </TableRow>}
                        </TableBody>
                    </Table>
                </div>
            </BPBoxWithHeader>
        </></>;
};



